import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import QRCode from 'qrcode.react';
import { withRouter } from 'react-router';

import { getAsset } from '../../../util/assets';
import AppStoreLink from '../../global/AppStoreLink';
import PlayStoreLink from '../../global/PlayStoreLink';
import XIcon from '../../icons/XIcon';
import ContextualString from '../../contextual/ContextualString';
import {
  ALBUM_PRICE_MODE,
  ALBUM_SUBTYPE,
  ALBUM_TYPE,
} from '../../../constants/albums';
import NativeAppFeatureListShutterbug from './NativeAppFeatureListShutterbug';
import NativeAppFeatureList from './NativeAppFeatureList';
import NativeAppFeatureListCamps from './NativeAppFeatureListCamps';
import NativeAppFeatureListSchools from './NativeAppFeatureListSchools';
import NativeAppFeatureListYouthSports from './NativeAppFeatureListYouthSports';
import ContributeButton from '../../global/ContributeButton';
import Button from '../../global/Button';
import { UsernameSelectModal } from '../../../apps/web-gallery/components/UsernameSelect';
import WaldoSpeechBubble from '../../global/WaldoSpeechBubble';
import { isIOS, isMobile } from '../../../util/device-detector';
import getConfig from '../../../util/config';
import {
  albumFolderIsPrePaid,
  isShutterbugDeliveryEnabled,
} from '../../../util/album-folder';

class PurchaseSuccess extends Component {
  static propTypes = {
    stringContext: PropTypes.shape({
      album_type: PropTypes.string,
      album_subtype: PropTypes.string,
    }),
    purchasedSubscription: PropTypes.bool,
    history: PropTypes.shape({
      push: PropTypes.func,
    }),
  };

  static defaultProps = {
    renderMobileAppUpsell: true,
  };

  render() {
    const {
      stringContext,
      history,
      purchasedSubscription,
      purchasedPrepaidItems,
    } = this.props;
    const config = getConfig();
    const appUrl = isIOS()
      ? config.mobile.iosAppUrl
      : config.mobile.androidAppUrl;

    const isPpd =
      stringContext?.album_price_mode === ALBUM_PRICE_MODE.PPD_ONLY ||
      stringContext?.picture_day_enabled;

    const featureList = (() => {
      if (stringContext?.album_type === ALBUM_TYPE.SHUTTERBUG) {
        return <NativeAppFeatureListShutterbug />;
      }

      if (stringContext?.album_subtype === ALBUM_SUBTYPE.CAMPS) {
        return <NativeAppFeatureListCamps />;
      }

      if (stringContext?.album_subtype === ALBUM_SUBTYPE.SCHOOLS) {
        return <NativeAppFeatureListSchools />;
      }

      if (stringContext?.album_subtype === ALBUM_SUBTYPE.YOUTH_SPORTS) {
        return <NativeAppFeatureListYouthSports />;
      }

      return <NativeAppFeatureList />;
    })();
    const shouldRenderNativeAppCta =
      purchasedSubscription ||
      albumFolderIsPrePaid(stringContext) ||
      stringContext?.album_type === ALBUM_TYPE.SHUTTERBUG ||
      isShutterbugDeliveryEnabled(stringContext);

    if (purchasedPrepaidItems) {
      return (
        <div className="d-flex flex-column w-100 flex-auto">
          <WaldoSpeechBubble
            position="bottom"
            className={purchasedPrepaidItems ? 'w-100-i' : ''}
          >
            As soon as your proofs are ready, I’ll send you a link to view your
            proofs and complete your order! You must select your preferred
            photo(s) to complete your order.
          </WaldoSpeechBubble>
        </div>
      );
    }

    return (
      <div className="d-flex flex-column w-100 flex-auto">
        <div className="purchase-success-container">
          <ContributeButton
            fallbackContent={
              <WaldoSpeechBubble position="bottom">
                <p className="text-center mb-0">
                  <ContextualString
                    id="subscriptionSuccessMainText"
                    type={stringContext?.album_type}
                    subtype={stringContext?.album_subtype}
                    defaultMessage="Sit back and relax... as soon as your photos are found, you will receive a text or email notification!"
                  />
                </p>
              </WaldoSpeechBubble>
            }
          >
            {({ onClick }) => (
              <Fragment>
                <WaldoSpeechBubble position="bottom" className="mb-2">
                  <p className="text-center mb-0">
                    <ContextualString
                      id="subscriptionSuccessMainText"
                      type={stringContext?.album_type}
                      subtype={stringContext?.album_subtype}
                      defaultMessage="Sit back and relax... as soon as your photos are found, you will receive a text or email notification!"
                    />
                  </p>
                </WaldoSpeechBubble>
                <div className="d-flex align-items-center flex-column justify-content-center">
                  <p className="text-center">
                    Add some of your photos from the event and I&apos;ll share
                    them!
                  </p>
                  <Button onClick={onClick} className="px-6">
                    Add My Photos
                  </Button>
                </div>
              </Fragment>
            )}
          </ContributeButton>
          {!stringContext?.picture_day_enabled && (
            <div className="d-flex align-items-center flex-column justify-content-center mt-1">
              <Button
                onClick={() =>
                  history.push(
                    `/app/shutterbug/hierarchy-items/${stringContext.id}`,
                  )
                }
                btnStyle="link"
                className="px-6"
              >
                Visit the event gallery
              </Button>
            </div>
          )}

          {shouldRenderNativeAppCta && (
            <Fragment>
              <hr className="my-2 w-100" />
              <p className="text-extra-bold text-primary text-center mb-0">
                Want to share your photos? <br />
                Download the Waldo app!
              </p>
              {isMobile() && (
                <Fragment>
                  <div className="purchase-success-app-icons single-icon">
                    {isIOS() ? (
                      <AppStoreLink width={160} />
                    ) : (
                      <PlayStoreLink width={160} />
                    )}
                  </div>
                  <ul className="purchase-success-feature-list">
                    {featureList}
                  </ul>
                </Fragment>
              )}
              {!isMobile() && (
                <div className="d-flex my-2">
                  <QRCode renderAs="canvas" value={appUrl} size={150} />
                  <ul className="purchase-success-feature-list ml-2">
                    {featureList}
                  </ul>
                </div>
              )}
            </Fragment>
          )}

          {!isPpd && (
            <Fragment>
              <div className="text-center text-xs mt-auto">
                {shouldRenderNativeAppCta ? (
                  <Fragment>
                    <span className="text-dark text-extra-bold">Note:</span>{' '}
                    Waldo turns off photo alerts between 11 p.m. and 7 a.m. so
                    he won&rsquo;t disrupt your evening! Any photos matched
                    during that time will still be visible in the Waldo app!
                  </Fragment>
                ) : (
                  <Fragment>
                    <span className="text-dark text-extra-bold">Note:</span>{' '}
                    Waldo turns off photo alerts between 11 p.m. and 7 a.m. so
                    he won&rsquo;t disrupt your evening!
                  </Fragment>
                )}
              </div>
              <div className="text-center mt-2">
                <div className="purchase-success-social-icons">
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.facebook.com/waldophotos"
                  >
                    <img
                      alt="Facebook Page"
                      src={getAsset('/icon-facebook.png')}
                      width={25}
                    />
                  </a>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.instagram.com/waldophotos"
                  >
                    <img
                      alt="Instagram Page"
                      src={getAsset('/icon-instagram.png')}
                      width={25}
                    />
                  </a>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://twitter.com/waldophotos"
                  >
                    <XIcon size={25} />
                  </a>
                </div>
              </div>
            </Fragment>
          )}
        </div>

        <UsernameSelectModal />
      </div>
    );
  }
}

export { PurchaseSuccess as PurchaseSuccessComponent };
export default withRouter(PurchaseSuccess);
